import React, { useEffect, useState, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import useWindowSize from '../../../hooks/useWindowSize';
import usePermissions from '../../../hooks/usePermissions'; 
import { isEmpty, orderBy } from 'lodash';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Grid, GridColumn as Column, GridNoRecords } from '@progress/kendo-react-grid';
import FormGroup from '@material-ui/core/FormGroup';
import BobWorkbookModeButton from '../../../components/BobWorkbookModeButton';
import { process as kendoProcess } from '@progress/kendo-data-query';

import BobRuleFilterComponent from '../../../components/BobRuleFilterComponent';
import Switch from '../../../components/FormFields/Switch';
import BobTableContextMenu from '../../../components/BobTableContextMenu';
import TotalsPanel from '../../../components/TotalsPanel';
import BobActionMenu from '../../../components/BobActionMenu';
import BobLoadingMask from '../../../components/BobLoadingMask';

import { dashboardContent } from '../../../localization/en-us/dashboardContent';
import {calculateMinColumnWidths} from '../../../helpers/calculateMinColumnWidths';

import RuleActionModal from '../Modals/RuleActionModal';

import * as BobActions from '../../../actions/bobActions';
import * as DashboardActions from '../../../actions/dashboardActions';
import * as RuleActions from '../../../actions/ruleActions';
import { kendoGridInlineStyling } from '../../../helpers/kendoGridInlineStyleHelper';
import classes from './Table.module.css';

const initialDataState = {
    sort:   [],
    take:   10,
    skip:   0,
    filter: null,
    group:  null
};


const RulesTable = ({
    bobLoading,
    activeNavCompany,
    deleteRule,
    fetchRuleList,
    fetchRuleKPIs,
    getRule,
    history,
    loading,
    rules,
    ruleMeta,
    kpis,
    saveRule,
    setActiveRule,
    setSelectedRule,
    setIsOpenDebugger,
    setIsOpenRuleBuilder,
    setIsOpenTargets,
    setWorkBookMode,
    selectedRule,
    workbookModeEnabled,
    ...props
}) => {
    const isFullRulesAllowed = usePermissions('rulesFull');
    const [selectedStateItems] = useState({});
    const [pageable] = useState({pageSizes: true});
    const [openRuleActionModal, setOpenRuleActionModal] = useState(false);
    const [menuActionClickType, setMenuActionClickType] = useState('');
    const [dataState, setDataState] = useState(initialDataState);
    const [ruleNameWidth, setRuleNameWidth] = useState(775);
    const [clearFilters, setClearFilters] = useState(false);
    const [filters, setFilters] = useState({
        clientId:   '',
        clientName: '',
        siteId:     '',
        siteName:   '',
        entityId:   '',
        entityName: ''
    });
    const [filteredBreadcrumb, setFilteredBreadbrumb] = useState('');
    const gridDivRef = useRef(null);
    const gridRef = useRef(null);

    const windowSize = useWindowSize();

    useEffect(() => {
        const gridColumns = gridRef.current.props.children[1];
        if (gridColumns) {
            const colWidth = calculateMinColumnWidths(gridColumns, gridDivRef.current.offsetWidth, 'name', 775, true);
            setRuleNameWidth(colWidth);
        }
    }, [windowSize]);

    useEffect(() => {
        if (!isEmpty(columns)) {
            const tempColumns = columns.map(c => {
                if (c.field === 'failureCount' || c.field === 'activeFaults') {
                    c.show = !isEmpty(filters.clientId);
                }
                return c;
            });
        
            const ruleNameCell = tempColumns.find(c => c.id === 'ruleNameCell');
            if (ruleNameCell) {
                const width = calculateMinColumnWidths(tempColumns.filter(c => c.show), gridDivRef.current.offsetWidth, 'name', 775, true);
                setRuleNameWidth(width);
            }
            setColumns(tempColumns);
        }
        

        let filterLabel = '';
        if (filters.clientId) {
            filterLabel = `${filters.clientName}`;
            if (filters.siteId) {
                filterLabel += ` -> ${filters.siteName}`;
            }
            if (filters.entityId) {
                filterLabel += ` -> ${filters.entityName}`;
            }
        }
        
        setFilteredBreadbrumb(filterLabel); 
    }, [rules]);

    async function init (isRefresh = false) {
        if (isEmpty(rules) || isRefresh) {
            const options = {pageSize: 20};
            setOpenRuleActionModal(false);

            const body = {
                clients:    filters.clientId ? [filters.clientId]: [],
                sites:      filters.siteId ? [filters.siteId]: [],
                equipments: filters.entityId ? [filters.entityId]: []
            };

            let promises = [fetchRuleKPIs(), fetchRuleList(activeNavCompany.id, body, options)];

            await Promise.all(promises.map(p => p.catch(err => err)));
        }
    }

    useEffect(() => {
        if (filters.clientId) {
            init(true);
        }
    }, [filters]);

    useEffect(() => {
        if (clearFilters) {
            init(true);
            setClearFilters(false);
        }
    }, [clearFilters]);

    useEffect(() => {
        if (!workbookModeEnabled) {
            setDataState(initialDataState);
        }
    }, [workbookModeEnabled]);

    const toggleIsEnabled = async (props, enabled) => {
        const rulePayload = await getRule(props.dataItem.id);
        let updatedRule = rulePayload.data;
        updatedRule.enabled = !enabled;
        await saveRule(updatedRule);

        if (!isEmpty(rules)) {
            rules.find(item => item.id === props.dataItem.id).enabled = !enabled;
        } 
        await fetchRuleKPIs();
    };

    const bulkToggle = async (rule, enable) => {
        const rulePayload = await getRule(rule.id);
        let updatedRule = rulePayload.data;
        updatedRule.enabled = enable;

        await saveRule(updatedRule);
        rules.find(item => item.id === rule.id).enabled = enable;
    };

    const enabledOrDisabledCell = props => {
        let checked = props.dataItem.enabled;
        return (
            <td className={props.className} style={props.style}>
                <div className={'announcement-switch'} style={{marginLeft: 40, paddingTop: 18, width: 80}}>
                    <FormGroup>
                        <FormControlLabel
                            control={ 
                                <Switch 
                                    label={''}
                                    checked={checked}
                                    size="small" 
                                    onChange={() => toggleIsEnabled(props, checked)}
                                />}
                        />
                    </FormGroup>
                </div>
            </td>
        );
    };

    const k2aOrCustomCell = props => {
        return (
            <td className={props.className} style={props.style}>
                { props.dataItem.custom ? 'Custom Rule' : 'K2A Rule'}
            </td>
        );
    };

    const nameCell = props => {
        return (
            <td className={props.className} style={props.style}>
                <div>
                    {props.dataItem[props.field]}
                </div>
            </td>
        );
    };

    const handleDeleteRule = async () => {
        let payload = selectedRule;
        await deleteRule(payload);

        init(true);
    };

    const handleDuplicateRule = async () => {
        const rulePayload = await getRule(selectedRule.id);
        let updatedRule = rulePayload.data;
        updatedRule.id = undefined;
        updatedRule.name = `Copy of ${updatedRule.name}`;
        await saveRule(updatedRule);

        init(true);
    };

    const handleRuleBuilderMenu = (rule, type) => {
        setActiveRule(rule);
        if ( type === 'manage') {
            history.push({
                pathname: `/rules/${rule.id}/edit/properties`,
                state:    { 'returnUrl': `/v2/dashboard/rules` }
            });
        } else if ( type === 'debugger') {
            setIsOpenDebugger(true);
        } else if ( type === 'viewTargets') {
            setIsOpenTargets(true);
        } else if (type === 'delete') {
            setOpenRuleActionModal(true);
            setMenuActionClickType(type);
        } else if (type === 'duplicate') {
            setOpenRuleActionModal(true);
            setMenuActionClickType(type);
        }
    };

    const actionsCell = props => {
        return (
            <td className={props.className} style={props.style}>
                <BobTableContextMenu 
                    type="RuleList"
                    ruleData={props.dataItem}
                    handleRuleBuilderMenu={(rule, type) => handleRuleBuilderMenu(rule, type)}
                    isFullRuleAllowed={isFullRulesAllowed}
                />
            </td>
        );
    };

    const handleActionButtonClick = (actionType) => {
        if (actionType === 'addRule') {
            history.push({
                pathname: `/rules/new/properties`,
                state:    { 'returnUrl': `/v2/dashboard/rules` }
            });
        } else if (actionType === 'createRule') {
            setIsOpenRuleBuilder(true);
        } else {
            setOpenRuleActionModal(true);
            setMenuActionClickType(actionType);
        }
    };

    const handleCloseRuleInfoModal = () => {
        setOpenRuleActionModal(false);
        setSelectedRule(null);
    };

    // action menu click for selected rules
    const handleMultipleRuleActionConfirm = async () => {
        let selectedItems = [];

        loading();
        
        for (const id in selectedStateItems) {
            selectedItems.push(selectedStateItems[id]);
        }

        if (menuActionClickType === 'enable') {
            await Promise.all(
                selectedItems.map(async (item, id) => {
                    await bulkToggle(item, true);
                })
            ).then(async (result) => {
                loading();
                await fetchRuleKPIs();
                setOpenRuleActionModal(false);
            });
        } else if (menuActionClickType === 'disable') {
            await Promise.all(
                selectedItems.map(async (item, id) => {
                    await bulkToggle(item, false);
                })
            ).then(async (result) => {
                loading();
                await fetchRuleKPIs();
                setOpenRuleActionModal(false);
            });
        } else if (menuActionClickType === 'duplicate') {
            Promise.all(
                selectedItems.map(async (item, id) => {
                    const rulePayload = await getRule(item.id);
                    let updatedRule = rulePayload.data;
                    updatedRule.id = undefined;
                    updatedRule.name = `Copy of ${item.name}`;

                    await saveRule(updatedRule, 'BobRuleBuilder');
                })
            ).then((result) => {
                loading();
                init(true);
            });
        } else if (menuActionClickType === 'delete') {
            Promise.all(
                selectedItems.map(async (item, id) => {
                    selectedItems[id].enabled = false;
                    await deleteRule(item, 'BobRuleBuilder');
                })
            ).then((result) => {
                loading();
                init(true);
            });
        }
    };

    const refreshGrid = () => {
        init(true);
    };

    const onDataStateChange = e => {
        setDataState(e.dataState);
    };

    const handleWorkbookMode = () => {
        setWorkBookMode(!workbookModeEnabled);
    };

    const defaultColumns = [
        {
            title:      dashboardContent.rule,
            field:      'name',
            cell:       nameCell,
            filter:     'text',
            width:      775,
            id:         'ruleNameCell',
            show:       true,
            orderIndex: 0
        },
        {
            title:      dashboardContent.k2aOrCustom,
            field:      'custom',
            width:      300,
            cell:       k2aOrCustomCell,
            filter:     'boolean',
            show:       true,
            orderIndex: 1
        },
        {
            title:      dashboardContent.targetCount,
            field:      'targetCount',
            width:      175,
            filter:     'numeric',
            show:       true,
            orderIndex: 2
        },
        {
            title:      dashboardContent.failureCount,
            field:      'failureCount',
            width:      175,
            filter:     'numeric',
            show:       false,
            orderIndex: 3
        },
        {
            title:      dashboardContent.activeFaults,
            field:      'activeFaults',
            width:      175,
            filter:     'numeric',
            show:       false,
            orderIndex: 4
        },
        {
            title:      'Enabled',
            field:      'enabled',
            width:      150,
            cell:       enabledOrDisabledCell,
            filter:     'boolean',
            show:       true,
            orderIndex: 5,
            id:         'enabledCell'
        },
        {
            title:      '',
            field:      '',
            actionCell: true,
            width:      75,
            locked:     true,
            cell:       actionsCell,
            show:       true,
            orderIndex: 1000
        }
    ];
    const [columns, setColumns] = useState(defaultColumns);

    return (
        <React.Fragment>
            <style>
                { kendoGridInlineStyling }
            </style>
            <div ref={gridDivRef} className={classes.Table}>
                <div className={classes.TableContainerHead}>
                    <div className={classes.SearchContainer}>
                        {filteredBreadcrumb}
                    </div>
                    <div className={classes.WorkbookContainer}>
                        <BobWorkbookModeButton
                            handleWorkbookMode={handleWorkbookMode}
                            workbookMode={workbookModeEnabled}
                        />
                    </div>
                    <div className={classes.TotalsContainer}>
                        <TotalsPanel
                            data={kpis}
                            entityType={'RuleList'}
                            filteredRules={filters.clientId}
                        />
                    </div>
                    <div className={classes.FilterContainer}>
                        <BobRuleFilterComponent 
                            activeNavCompany= {activeNavCompany}
                            filters={filters}
                            isFromRuleDebugger={true}
                            setFilters={setFilters}
                            isFromRulesTable={true}
                            setClearFilters={setClearFilters}
                        />
                    </div>
                    <div
                        className={classes.RefreshContainer}
                        aria-hidden="true"
                        role="button"
                        tabIndex={0}
                        onClick={refreshGrid}
                    >
                        <span className="material-icons-outlined">refresh</span>
                    </div>
                    <div className={classes.ActionButtonContainer}>
                        { isFullRulesAllowed ? 
                            <BobActionMenu 
                                type={'RuleList'}
                                isDisableRuleListMenu={isEmpty(selectedStateItems)}
                                handleListAction={(menuType, actionType) => handleActionButtonClick(menuType, actionType)}
                            />
                            : null}
                    </div>
                </div>
                <Grid
                    ref={gridRef}
                    sortable={{
                        mode: 'multiple'
                    }}
                    className={classes.KendoGrid}
                    data={kendoProcess(rules, dataState)}
                    {...dataState}
                    pageable={pageable}
                    reorderable
                    dataItemKey={'id'}
                    filterable={workbookModeEnabled}
                    onDataStateChange={onDataStateChange}
                >
                    {
                        bobLoading.includes('fetchRuleList') ?
                            <GridNoRecords>
                                Loading rules
                            </GridNoRecords>
                            :
                            null
                    }
                    {
                        orderBy(columns, 'orderIndex', 'asc').map(
                            (column, index) =>
                                column.show && (
                                    <Column
                                        key={index}
                                        field={column.field}
                                        title={column.title}
                                        cell={column.id === 'enabledCell' ? enabledOrDisabledCell : column.cell}
                                        filter={column.filter}
                                        sortable={column.sortable}
                                        filterable={column.filterable}
                                        width={column.id === 'ruleNameCell' ? ruleNameWidth : column.width}
                                        locked={column.locked}
                                    />
                                )
                        )
                    }
                </Grid>
                {
                    openRuleActionModal ?
                        <RuleActionModal
                            menuActionClickType={menuActionClickType}
                            onCloseModal={() => handleCloseRuleInfoModal()}
                            selectedItems={selectedStateItems}
                            selectedRule={selectedRule}
                            isContextMenuClick={ selectedRule ? true : false}
                            handleDeleteRule={handleDeleteRule}
                            handleDuplicateRule={handleDuplicateRule}
                            handleMultipleRuleActionConfirm={handleMultipleRuleActionConfirm}
                        />
                        :
                        null
                }
                {
                    bobLoading.includes('fetchRuleList') 
                    || bobLoading.includes('deleteRule') 
                    || bobLoading.includes('enableOrDisableOrDuplicateRuleFromRuleBuilderPage')
                    || bobLoading.includes('actionMenuClickFromRuleBuilderPage') ?
                        <BobLoadingMask />
                        :
                        null
                }
            </div>
        </React.Fragment>
    );
};

const mapStateToProps = state => ({
    bobLoading:          state.bob.bobLoading,
    activeNavCompany:    state.bob.activeNavCompany,
    rules:               state.rules.ruleList,
    ruleMeta:            state.rules.ruleMeta,
    kpis:                state.rules.kpis,
    workbookModeEnabled: state.dashboard.workbookModeEnabled
});

const mapDispatchToProps = ({
    ...BobActions,
    ...DashboardActions,
    ...RuleActions
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RulesTable));
